// src/firebase.js
import { initializeApp } from "firebase/app";
import { getAnalytics, isSupported as isAnalyticsSupported } from "firebase/analytics";

import {
  initializeAuth,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  inMemoryPersistence,
  browserPopupRedirectResolver,
  connectAuthEmulator,
} from "firebase/auth";

import {
  initializeFirestore,
  connectFirestoreEmulator,
} from "firebase/firestore";

import {
  getStorage,
  connectStorageEmulator,
} from "firebase/storage";

// -----------------------------------------------------
// 🔧 Firebase-Konfiguration
// -----------------------------------------------------
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// -----------------------------------------------------
// ✅ App Init
// -----------------------------------------------------
export const app = initializeApp(firebaseConfig);

export const analyticsPromise =
  typeof window !== "undefined"
    ? isAnalyticsSupported().then((ok) => (ok ? getAnalytics(app) : null))
    : Promise.resolve(null);

// -----------------------------------------------------
// ✅ Auth Init
// -----------------------------------------------------
export const auth = initializeAuth(app, {
  persistence: [
    indexedDBLocalPersistence,
    browserLocalPersistence,
    inMemoryPersistence,
  ],
  popupRedirectResolver: browserPopupRedirectResolver,
});

// -----------------------------------------------------
// ✅ Firestore Init
// - iOS WebView bekommt Long Polling
// -----------------------------------------------------
const isIOSWebView =
  typeof navigator !== "undefined" &&
  /iPhone|iPad|iPod/i.test(navigator.userAgent || "");

export const db = isIOSWebView
  ? initializeFirestore(app, {
      experimentalForceLongPolling: true,
      useFetchStreams: false,
      ignoreUndefinedProperties: true,
    })
  : initializeFirestore(app, {
      ignoreUndefinedProperties: true,
    });

// -----------------------------------------------------
// ✅ Storage Init
// -----------------------------------------------------
export const storage = getStorage(app);

// -----------------------------------------------------
// 🧪 Emulatoren (optional lokal)
// -----------------------------------------------------
const useEmulators =
  process.env.REACT_APP_FIREBASE_USE_EMULATORS === "true" &&
  typeof window !== "undefined" &&
  window.location.hostname === "localhost";

if (useEmulators) {
  console.info("[Firebase] Nutze Emulatoren (Auth, Firestore, Storage).");

  try {
    connectAuthEmulator(auth, "http://127.0.0.1:9099");
  } catch (e) {
    console.warn("[Firebase] Auth-Emulator konnte nicht verbunden werden:", e);
  }

  try {
    connectFirestoreEmulator(db, "127.0.0.1", 8080);
  } catch (e) {
    console.warn("[Firebase] Firestore-Emulator konnte nicht verbunden werden:", e);
  }

  try {
    connectStorageEmulator(storage, "127.0.0.1", 9199);
  } catch (e) {
    console.warn("[Firebase] Storage-Emulator konnte nicht verbunden werden:", e);
  }
}

// -----------------------------------------------------
// ✅ Debug
// -----------------------------------------------------
// -----------------------------------------------------
// ✅ Debug (nur Development)
// -----------------------------------------------------
if (process.env.NODE_ENV === "development") {
  console.log("🔥 Firebase projectId:", app.options.projectId);
  console.log(
    "🔥 Firestore init mode:",
    isIOSWebView ? "iOS forced long polling" : "default"
  );
}